import { createContext, useState, PropsWithChildren } from 'react';
import { contentServices } from '~/services';
import { ProjectContext } from '../project';
import { getQueryParams } from '~/utils/getQueryParams';
import { reportServices } from '../../services';
import { useEntityContext, useFloatMenu } from '~/hooks';
import { ProjectProviderInterface } from '~/interfaces/contexts';
import { SORT_TYPE_OPTIONS } from '~/constants/SortTypeOptions.enum';

const queryParams = getQueryParams('params');

export const ReportsContext = createContext();

const ReportsProvider = ({ children }: PropsWithChildren) => {
    const { selectedProject, projects } =
        useEntityContext<ProjectProviderInterface>(ProjectContext);

    // REPORTS
    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);

    // SECTIONS
    const [loadingSections, setLoadingSections] = useState(true);
    const [reportSections, setReportSections] = useState([]);
    const [selectedSource, setSelectedSource] = useState(null);

    const newReportMenu = useFloatMenu();

    // ------- REPORTS -------------

    const getReports = async (newReportCreated) => {
        setLoading(true);

        const [error, response] = await reportServices.getReports({
            page_size: 10000,
            ...(selectedProject && { project_ids: selectedProject.id }),
        });

        setLoading(false);

        if (error) return;

        const { data } = response;
        setReports(data);

        if (newReportCreated) {
            setSelectedReport(newReportCreated);
            return;
        }

        // if there is aselectedReport, select it again (just in case it was edited)
        if (selectedReport) {
            const current = data.find((r) => r.id === selectedReport.id);

            if (current) {
                setSelectedReport(current);
                return;
            }
        }
        // check if there is a query link
        if (queryParams?.report) {
            const queryReport = data.find((p) => p.id === queryParams.report);
            queryReport && setSelectedReport(queryReport);
            return;
        }

        setSelectedReport(data[0] || null);
    };

    const onCreateReport = async (reportName, projectId) => {
        setLoading(true);

        const [error, response] = await reportServices.createReport(
            reportName,
            projectId
        );

        setLoading(false);

        if (!error) {
            const { data } = response;
            getReports(data);
        }

        return { error };
    };

    const onRemoveReport = async (reportId) => {
        setLoading(true);

        const [error, response] = await reportServices.deleteReport(reportId);

        setLoading(false);

        if (!error) getReports();

        return response;
    };

    const onUpdateReport = async (topicId, topicName) => {
        setLoading(true);

        const [error, response] = await contentServices.updateTopic(topicId, {
            name: topicName,
        });

        setLoading(false);

        if (!error) getReports();

        return response;
    };

    // ------- SECTIONS -------------

    const getReportSections = async () => {
        setLoadingSections(true);

        const [error, response] = await reportServices.getReportSections({
            report_ids: selectedReport.id,
            page_size: 10000,
            sort_attribute: 'position',
            sort_order: SORT_TYPE_OPTIONS.DESC,
        });

        setLoadingSections(false);

        if (error) return { error: true };

        const { data } = response;
        setReportSections(data);
        return { error: false };
    };

    const onCreateSection = async (section) => {
        setLoadingSections(true);

        const [error] = await reportServices.createReportSection(section);

        setLoadingSections(false);

        if (error) return { error: true };

        return await getReportSections();
    };

    const onRemoveSection = async (sectionId) => {
        setLoadingSections(true);

        const [error] = await reportServices.deleteSection(sectionId);

        setLoadingSections(false);

        if (error) return { error: true };

        return await getReportSections();
    };

    const onUpdateSection = async (sectionId, payload) => {
        setLoadingSections(true);

        const [error] = await reportServices.updateSection(sectionId, payload);

        setLoadingSections(false);

        if (error) return { error: true };

        return await getReportSections();
    };

    // ------- SOURCES -------------

    return (
        <ReportsContext.Provider
            value={{
                loading,
                reports,
                selectedReport,
                projects,
                selectedProject,
                sections: reportSections,
                newReportMenu,
                loadingSections,
                selectedSource,
                setLoading,
                setSelectedSource,
                setSelectedReport,
                onCreateReport,
                onRemoveReport,
                onUpdateReport,
                onCreateSection,
                onRemoveSection,
                onUpdateSection,
                getReports,
                getReportSections,
                setReports,
                setReportSections,
            }}
        >
            {children}
        </ReportsContext.Provider>
    );
};

export default ReportsProvider;
